import InterfaceHelper from '@unifyapps/carbon/no-code/stores/InterfaceStore/helper/InterfaceHelper';
import type {
  ActionNavItem,
  AppearanceNavItem,
  AppearanceNavItems,
  EntityDetails,
  GroupNavItem,
  InterfaceEntity,
  NavItem,
  NavItems,
  Navigation,
  NavigationItem,
  PageNavItem,
  UrlNavItem,
  UserMenuNavItem,
} from '@unifyapps/defs/types/interface';
import {
  ApplicationType,
  NavigationBarType,
  NavigationItemLabelVariant,
  NavigationPosition,
  NavigationResolution,
  NavigationSelectionVariant,
  NavigatorType,
} from '@unifyapps/defs/types/interface';
import { NAVIGATION_TOKENS } from '@unifyapps/defs/types/interface/components/Navigation';
import { EXPANDED_SOLID_NAVIGATION_TOKENS } from '@unifyapps/defs/types/interface/components/Navigation/consts';
import type { NavigationTheme } from '@unifyapps/defs/types/interface/components/Navigation/types';
import { ThemeComponents } from '@unifyapps/defs/types/interface/types';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import { BlockThemeEnum, type StyleObject } from '@unifyapps/defs/types/styleObject';
import { getNumericValue } from '@unifyapps/style/helpers/cssToNumber';
import { produce } from 'immer';
import _omit from 'lodash/omit';
import _pickBy from 'lodash/pickBy';
import {
  NAVBAR_INITIAL_BOTTOM_MOBILE_STYLES,
  NAVBAR_INITIAL_BOTTOM_STYLES,
  NAVBAR_INITIAL_START_STYLES,
} from '../../components/NavigationContainer/constants';
import { getInterfaceCssVarElementStyles } from '../../theme/views/builder/utils';

const DEFAULT_BREAKPOINT_VISIBILITY = [
  NavigationResolution.XL,
  NavigationResolution.LG,
  NavigationResolution.MD,
  NavigationResolution.SM,
];

type WithVisibilityAndOverflow<T> = Omit<T, 'items'> & {
  hiddenInOverflow?: boolean;
  visible?: boolean;
};

export enum NavigationItemCategory {
  Primary = 'primary',
  Secondary = 'secondary',
  UserMenu = 'userMenu',
}

export type NavItemWithVisibilityAndOverflow =
  | WithVisibilityAndOverflow<UrlNavItem>
  | WithVisibilityAndOverflow<PageNavItem>
  | WithVisibilityAndOverflow<UserMenuNavItem>
  | WithVisibilityAndOverflow<ActionNavItem>
  | (WithVisibilityAndOverflow<GroupNavItem> & {
      items: NavItemWithVisibilityAndOverflow[];
    });

export type NavItemsWithVisibilityAndOverflow = NavItemWithVisibilityAndOverflow[];

export type VisibilityHelpersType = {
  getNavItemVisibility: (
    navItem: NavItemWithVisibilityAndOverflow,
    category: NavigationItemCategory,
  ) => boolean;
  toggleNavItemVisibility: (
    navItem: NavItemWithVisibilityAndOverflow,
    category: NavigationItemCategory,
    prevVisibility: boolean,
  ) => void;
};

// If visible prop is present, then we use it to determine the visibility of the navItem
// else we use the breakpoint visibility
export const getVisibleItems = (
  items: NavItemsWithVisibilityAndOverflow,
  resolution: NavigationResolution,
  navItemsMap?: Map<string, NavItem>,
): NavItemsWithVisibilityAndOverflow => {
  return items
    .filter((item): item is NavItemWithVisibilityAndOverflow => {
      const breakpointVisibility = navItemsMap?.get(item.id)?.breakpointVisibility;

      // for backward compatibility, 'visible' property present in old NavItems
      const isHidden = Boolean('visible' in item && !item.visible);

      // hiddenInOverflow handling to be added later
      if (
        item.hiddenInOverflow ||
        isHidden ||
        (!('visible' in item) && breakpointVisibility && !breakpointVisibility.includes(resolution))
      ) {
        return false;
      }

      return true;
    })
    .map((item) => {
      if (item.type === 'GROUP') {
        return {
          ...item,
          items: getVisibleItems(item.items, resolution, navItemsMap),
        };
      }

      return item;
    });
};

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- false positive
export class InterfaceNavigationHelper {
  static getRawNonNormalizedPagePathOrSlugById(
    interfaceRecord: InterfaceEntity,
    pageId: string,
  ): string | undefined {
    const path = InterfaceHelper.getFullRawNonNormalizedPathByPageId(pageId, interfaceRecord);
    return path ?? InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord)?.[pageId];
  }

  static dedupePageNavItems(navItems: NavItem[], seenPageIds = new Set<string>()): NavItem[] {
    const dedupedNavItems: NavItem[] = [];

    for (const navItem of navItems) {
      if (navItem.type === 'GROUP') {
        const items = InterfaceNavigationHelper.dedupePageNavItems(
          (navItem.items as NavItem[]) ?? [],
          seenPageIds,
        );
        if (items.length === 0) continue;
        dedupedNavItems.push({ ...navItem, items } as NavItem);
        continue;
      }

      if (navItem.type === 'PAGE') {
        if (seenPageIds.has(navItem.pageId)) continue;
        seenPageIds.add(navItem.pageId);
      }

      dedupedNavItems.push(navItem);
    }

    return dedupedNavItems;
  }

  static getPrimaryNavigationByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
    applicationType?: ApplicationType,
  ) {
    const originalNavItems = interfaceRecord.properties.navigation.primary;
    const resolutionNavItems =
      interfaceRecord.properties.navigation.appearance?.[resolution]?.primary;

    let items = resolutionNavItems
      ? getVisibleItems(
          InterfaceNavigationHelper.transformAppearanceNavItemToNavItems({
            appearanceItems: resolutionNavItems,
            originalNavItems,
          }),
          resolution,
          InterfaceNavigationHelper.createNavItemsMap(originalNavItems),
        )
      : originalNavItems;

    if (applicationType === ApplicationType.MOBILE && items) {
      items = InterfaceNavigationHelper.dedupePageNavItems(items);
    }

    return items;
  }

  static getBrandNavigationItem(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
  ) {
    const primaryNavItems = InterfaceNavigationHelper.getPrimaryNavigationByResolution(
      interfaceRecord,
      resolution,
    );
    const brandNavigationItem = primaryNavItems.find((item) => item.id === 'BRAND_NAVIGATION');

    return {
      ...brandNavigationItem,
      name: interfaceRecord?.properties.name ?? '',
      logo: {
        small: interfaceRecord?.properties.metadata.logo.small ?? '',
        large: interfaceRecord?.properties.metadata.logo.large ?? '',
      },
    };
  }

  static getUserMenuItem(interfaceRecord: InterfaceEntity, resolution: NavigationResolution) {
    const secondaryNavItems = InterfaceNavigationHelper.getSecondaryItemsByResolution(
      interfaceRecord,
      resolution,
    );

    const userMenuItem = secondaryNavItems?.find((item) => item.id === 'userMenu');

    return userMenuItem;
  }

  static getSecondaryItemsByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
    applicationType?: ApplicationType,
  ) {
    if (!interfaceRecord.properties.navigation.secondary) {
      return undefined;
    }

    const originalNavItems = interfaceRecord.properties.navigation.secondary;

    let secondaryItems = interfaceRecord.properties.navigation.appearance?.[resolution]?.secondary
      ? getVisibleItems(
          InterfaceNavigationHelper.transformAppearanceNavItemToNavItems({
            appearanceItems: interfaceRecord.properties.navigation.appearance[resolution].secondary,
            originalNavItems,
          }),
          resolution,
          InterfaceNavigationHelper.createNavItemsMap(originalNavItems),
        )
      : interfaceRecord.properties.navigation.secondary;

    const isPublicInterface =
      InterfaceHelper.getInterfaceSecurityDetails(interfaceRecord)?.type === 'PUBLIC';

    if (isPublicInterface) {
      secondaryItems = secondaryItems?.filter((item) => item.id !== 'userMenu');
    }

    if (applicationType === ApplicationType.MOBILE && secondaryItems) {
      secondaryItems = InterfaceNavigationHelper.dedupePageNavItems(secondaryItems);
    }

    return secondaryItems;
  }

  static getUserMenuItemsByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
    applicationType?: ApplicationType,
  ) {
    const originalNavItems = interfaceRecord.properties.navigation.secondary?.find(
      (item) => item.type === 'USER_MENU',
    )?.menuItems;

    if (!originalNavItems) {
      return undefined;
    }

    let items: NavItem[] | undefined = interfaceRecord.properties.navigation.appearance?.[
      resolution
    ]?.userMenu
      ? getVisibleItems(
          InterfaceNavigationHelper.transformAppearanceNavItemToNavItems({
            appearanceItems: interfaceRecord.properties.navigation.appearance[resolution].userMenu,
            originalNavItems,
          }),
          resolution,
          InterfaceNavigationHelper.createNavItemsMap(originalNavItems),
        )
      : undefined;

    if (applicationType === ApplicationType.MOBILE && items) {
      items = InterfaceNavigationHelper.dedupePageNavItems(items);
    }

    return items;
  }

  static getBarTypeByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
  ): NavigationBarType {
    const navAppearance = interfaceRecord.properties.navigation.appearance;

    if (!navAppearance?.[resolution]) {
      return NavigationBarType.Fixed;
    }

    return navAppearance[resolution].navigationBar.type;
  }

  static getPositionByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
  ): NavigationPosition {
    const navAppearance = interfaceRecord.properties.navigation.appearance;

    if (!navAppearance?.[resolution]) {
      return resolution === NavigationResolution.SM
        ? NavigationPosition.Bottom
        : NavigationPosition.Start;
    }

    return navAppearance[resolution].navigationBar.position;
  }

  static getNavigatorTypeByResolution(
    interfaceRecord: InterfaceEntity | undefined,
    resolution: NavigationResolution,
    applicationType?: ApplicationType,
  ): NavigatorType {
    const navigatorType =
      interfaceRecord?.properties.navigation.appearance?.[resolution]?.navigationBar.navigatorType;

    if (applicationType === ApplicationType.MOBILE) {
      return navigatorType ?? NavigatorType.BottomTabsNavigator;
    }

    return navigatorType ?? NavigatorType.Standard;
  }

  static getFloatingOffsetByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
  ): Pick<StyleObject, 'margin'> | undefined {
    const navAppearance = interfaceRecord.properties.navigation.appearance;

    if (navAppearance?.[resolution]) {
      return 'floatingOffset' in navAppearance[resolution].navigationBar
        ? navAppearance[resolution].navigationBar.floatingOffset
        : undefined;
    }

    return undefined;
  }

  static getStylesByResolution(
    interfaceRecord: InterfaceEntity | undefined,
    resolution: NavigationResolution,
    position?: NavigationPosition,
    applicationType?: ApplicationType,
  ) {
    const navAppearance = interfaceRecord?.properties.navigation.appearance;
    const styles = navAppearance?.[resolution]?.styles;
    const navigatorType = InterfaceNavigationHelper.getNavigatorTypeByResolution(
      interfaceRecord,
      resolution,
      applicationType,
    );
    if (styles) {
      return styles;
    }

    if (resolution === NavigationResolution.SM && applicationType === ApplicationType.MOBILE) {
      return navigatorType === NavigatorType.BottomTabsNavigator
        ? NAVBAR_INITIAL_BOTTOM_MOBILE_STYLES
        : undefined;
    }

    return position === NavigationPosition.Bottom
      ? NAVBAR_INITIAL_BOTTOM_STYLES
      : NAVBAR_INITIAL_START_STYLES;
  }

  static getHeightOrWidthByResolution(
    interfaceRecord: InterfaceEntity | undefined,
    resolution: NavigationResolution,
    position?: NavigationPosition,
    applicationType?: ApplicationType,
    type: 'height' | 'width' = 'height',
  ): number | undefined {
    const styles = InterfaceNavigationHelper.getStylesByResolution(
      interfaceRecord,
      resolution,
      position,
      applicationType,
    );
    if (type === 'height') {
      if (!styles?.height) return undefined;
      const heightValue = typeof styles.height === 'string' ? styles.height : styles.height.custom;
      if (!heightValue) return undefined;
      return getNumericValue(heightValue);
    }
    if (!styles?.width) return undefined;
    const widthValue = typeof styles.width === 'string' ? styles.width : styles.width.custom;
    if (!widthValue) return undefined;
    return getNumericValue(widthValue);
  }

  // To get complete navigation bar
  static getNavigationBarByResolution(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
    applicationType?: ApplicationType,
  ) {
    const position = InterfaceNavigationHelper.getPositionByResolution(interfaceRecord, resolution);
    const type = InterfaceNavigationHelper.getBarTypeByResolution(interfaceRecord, resolution);
    const floatingOffset = InterfaceNavigationHelper.getFloatingOffsetByResolution(
      interfaceRecord,
      resolution,
    );
    const overflowDetails = {
      icon: 'Menu01',
      label: 'More',
    };
    const navigatorType = InterfaceNavigationHelper.getNavigatorTypeByResolution(
      interfaceRecord,
      resolution,
      applicationType,
    );

    const navigatorModuleId =
      interfaceRecord.properties.navigation.appearance?.[resolution]?.navigationBar
        ?.navigatorModuleId;

    return {
      position,
      type,
      overflowDetails,
      navigatorType,
      navigatorModuleId,
      ...(type === NavigationBarType.Floating && { floatingOffset }),
    };
  }

  static getItemAppearance(
    interfaceRecord: InterfaceEntity,
    resolution: NavigationResolution,
  ): NavigationItem {
    const navAppearance = interfaceRecord.properties.navigation.appearance;

    if (!navAppearance?.[resolution]) {
      return {
        selectionVariant: NavigationSelectionVariant.Stroke,
        labelVariant:
          resolution !== NavigationResolution.XL
            ? NavigationItemLabelVariant.Upfront
            : NavigationItemLabelVariant.Expandable,
      };
    }

    // For non-desktop sidenav , Expandable sidenav not present
    // For backward compatible check, if labelVariant is Expandable, then change it to None
    if (resolution !== NavigationResolution.XL) {
      return {
        ...navAppearance[resolution].navigationItem,
        labelVariant:
          navAppearance[resolution].navigationItem.labelVariant ===
          NavigationItemLabelVariant.Expandable
            ? NavigationItemLabelVariant.None
            : navAppearance[resolution].navigationItem.labelVariant,
      };
    }

    return navAppearance[resolution].navigationItem;
  }

  static getThemeByResolution(interfaceRecord: InterfaceEntity, resolution: NavigationResolution) {
    const navAppearance = interfaceRecord.properties.navigation.appearance;
    return navAppearance?.[resolution]?.theme ?? BlockThemeEnum.INHERIT;
  }

  static showSideOrBottomNav(interfaceRecord: InterfaceEntity, resolution: NavigationResolution) {
    const navigatorType =
      interfaceRecord.properties.navigation.appearance?.[resolution]?.navigationBar.navigatorType;

    const disableSideBottomNavigation =
      (navigatorType === NavigatorType.None && resolution === NavigationResolution.SM) ||
      navigatorType === NavigatorType.DrawerNavigator;

    return !disableSideBottomNavigation;
  }

  static showDrawerNav(interfaceRecord: InterfaceEntity, resolution: NavigationResolution) {
    if (resolution !== NavigationResolution.SM) return false;

    const navigatorType =
      interfaceRecord.properties.navigation.appearance?.[resolution]?.navigationBar.navigatorType;

    return navigatorType === NavigatorType.DrawerNavigator;
  }

  static findNavItemByIdInNavItems = (navItems: NavItems | undefined, navItemId: string) => {
    const findRecursively = (items: NavItem[], currentNavItemId: string): NavItem | undefined => {
      for (const item of items) {
        if (item.id === currentNavItemId) {
          return item;
        }

        if (item.type === 'GROUP') {
          const found = findRecursively(item.items, currentNavItemId);
          if (found) {
            return found;
          }
        }
      }

      return undefined;
    };

    if (!navItems) {
      return undefined;
    }

    return findRecursively(navItems, navItemId);
  };

  static findNavItemInNavigation = (
    navigation: InterfaceEntity['properties']['navigation'],
    navItemId: string,
  ) => {
    const userMenuItems = navigation.secondary?.find(
      (item) => item.type === 'USER_MENU',
    )?.menuItems;

    return (
      InterfaceNavigationHelper.findNavItemByIdInNavItems(navigation.primary, navItemId) ??
      InterfaceNavigationHelper.findNavItemByIdInNavItems(navigation.secondary, navItemId) ??
      InterfaceNavigationHelper.findNavItemByIdInNavItems(userMenuItems, navItemId)
    );
  };

  /**
   * Returns the set of page slugs from nav items that are of type PAGE.
   * Used for tab/drawer route validation (e.g. root routes in React Native).
   * Handles nested GROUP items recursively.
   */
  static getTabRouteSlugs(navItems: NavItems | undefined): Set<string> {
    if (!navItems) return new Set();

    const collectPageSlugs = (items: NavItems): string[] => {
      const slugs: string[] = [];

      for (const item of items) {
        if (item.type === 'PAGE' && item.slug) {
          slugs.push(item.slug);
        } else if (item.type === 'GROUP') {
          // recursively collect slugs from nested items
          slugs.push(...collectPageSlugs(item.items));
        }
      }

      return slugs;
    };

    return new Set(collectPageSlugs(navItems));
  }

  static getItemsForResolution(navItems: NavItems, resolution: NavigationResolution) {
    const isVisibleForResolution = (item: NavItem): boolean => {
      if (!item.breakpointVisibility) {
        return true;
      }
      return item.breakpointVisibility.includes(resolution);
    };

    const filterNavItems = (items: NavItems): NavItems => {
      return items.reduce<NavItems>((acc, item) => {
        if (!isVisibleForResolution(item)) {
          return acc;
        }

        if (item.type === 'GROUP') {
          const filteredChildren = filterNavItems(item.items);

          if (filteredChildren.length > 0) {
            acc.push({
              ...item,
              items: filteredChildren,
            });
          }
          return acc;
        }

        acc.push(item);
        return acc;
      }, []);
    };

    return filterNavItems(navItems);
  }

  static syncNavigationAppearance = (
    navigation: InterfaceEntity['properties']['navigation'],
  ): InterfaceEntity['properties']['navigation'] => {
    if (!navigation.appearance) {
      return navigation;
    }

    const getAllNavItemIds = (items: NavItems): Set<string> => {
      const navItemsMap = InterfaceNavigationHelper.createNavItemsMap(items);
      return new Set(navItemsMap?.keys() ?? []);
    };

    // Returns Ids that exists before syncing for particular resolution
    const getExistingIds = (items: AppearanceNavItems): Set<string> => {
      return items.reduce((ids, item) => {
        ids.add(item.id);
        if (item.items) {
          for (const subItem of item.items) {
            ids.add(subItem.id);
          }
        }
        return ids;
      }, new Set<string>());
    };

    const createAppearanceItem = (navItem: NavItem): AppearanceNavItem => ({
      id: navItem.id,
      hiddenInOverflow: false,
      ...(navItem.type === 'GROUP'
        ? {
            items: navItem.items.map((item) => createAppearanceItem(item)),
          }
        : {}),
    });

    // Remove the NavItems that have been deleted
    const filterValidItems = (
      items: AppearanceNavItems,
      validIds: Set<string>,
    ): AppearanceNavItems => {
      return items.reduce<AppearanceNavItems>((filteredItems, item) => {
        if (!validIds.has(item.id)) return filteredItems;

        if (item.items) {
          const filteredSubItems = item.items.filter((subItem) => validIds.has(subItem.id));
          if (filteredSubItems.length === 0) {
            return filteredItems;
          }

          filteredItems.push({ ...item, items: filteredSubItems });
        } else {
          filteredItems.push(item);
        }

        return filteredItems;
      }, []);
    };

    const addMissingItems = (
      existingItems: AppearanceNavItems,
      navItems: NavItems,
      existingIds: Set<string>,
    ): AppearanceNavItems => {
      const newItems = navItems
        .filter((item) => !existingIds.has(item.id))
        .map(createAppearanceItem);

      // If new item is added in a group, search for that item in navItems and
      //  then add it to the existing items in same group
      const adaptedExistingItems = existingItems.map((item) => {
        const itemId = item.id;
        // Find item with itemId in navItems
        const navItemWithItemId = navItems.find((navItem) => navItem.id === itemId);

        if (item.items && navItemWithItemId && navItemWithItemId.type === 'GROUP') {
          const newSubItems = navItemWithItemId.items.filter(
            (subItem) => !existingIds.has(subItem.id),
          );
          if (newSubItems.length > 0) {
            return {
              ...item,
              items: [...item.items, ...newSubItems.map(createAppearanceItem)],
            };
          }
        }
        return item;
      });

      // Notifications and userMenu should always be at last
      const _adaptedNavItems = [...adaptedExistingItems, ...newItems];
      const notificationItem = _adaptedNavItems.find((item) => item.id === 'notifications');
      const userMenuItem = _adaptedNavItems.find((item) => item.id === 'userMenu');

      const adaptedNavItems = _adaptedNavItems.filter((item) => {
        return item.id !== 'notifications' && item.id !== 'userMenu';
      });

      return [
        ...adaptedNavItems,
        ...(notificationItem ? [notificationItem] : []),
        ...(userMenuItem ? [userMenuItem] : []),
      ];
    };

    const syncItems = (
      appearanceItems: AppearanceNavItems,
      navItems: NavItems,
      isUserReordered: boolean,
    ): AppearanceNavItems => {
      if (!isUserReordered) {
        return navItems.map(createAppearanceItem);
      }

      const validIds = getAllNavItemIds(navItems);
      const existingIds = getExistingIds(appearanceItems);

      return addMissingItems(filterValidItems(appearanceItems, validIds), navItems, existingIds);
    };

    const updatedAppearance = Object.entries(navigation.appearance).reduce<
      typeof navigation.appearance
    >((acc, [resolution, appearanceForResolution]) => {
      const isPrimaryUserReordered = appearanceForResolution.userReordered?.primary ?? false;
      const isSecondaryUserReordered = appearanceForResolution.userReordered?.secondary ?? false;
      const isUserMenuUserReordered = appearanceForResolution.userReordered?.userMenu ?? false;

      const updatedPrimary = syncItems(
        appearanceForResolution.primary,
        navigation.primary,
        isPrimaryUserReordered,
      );

      const updatedSecondary = navigation.secondary
        ? syncItems(
            appearanceForResolution.secondary || [],
            navigation.secondary,
            isSecondaryUserReordered,
          )
        : undefined;

      const userMenuNavItem = navigation.secondary?.find((item) => item.type === 'USER_MENU');
      const userMenuItems = userMenuNavItem?.menuItems;
      const updatedUserMenu = userMenuNavItem
        ? syncItems(
            appearanceForResolution.userMenu || [],
            userMenuItems ?? [],
            isUserMenuUserReordered,
          )
        : undefined;

      // When USER_MENU is removed from secondary, drop appearance.userMenu too; spreading
      // appearanceForResolution alone would keep stale per-resolution user menu items.
      acc[resolution] = {
        ...(userMenuNavItem ? appearanceForResolution : _omit(appearanceForResolution, 'userMenu')),
        primary: updatedPrimary,
        ...(updatedSecondary && { secondary: updatedSecondary }),
        ...(userMenuNavItem ? { userMenu: updatedUserMenu } : {}),
      };

      return acc;
    }, {});

    return {
      ...navigation,
      appearance: updatedAppearance,
    };
  };

  static transformNavItemsToAppearanceNavItem = ({
    navItems,
  }: {
    navItems: NavItemsWithVisibilityAndOverflow;
  }): AppearanceNavItems => {
    const createAppearanceItem = (
      navItem: NavItemWithVisibilityAndOverflow,
    ): AppearanceNavItem => ({
      id: navItem.id,
      hiddenInOverflow: navItem.hiddenInOverflow,
      ...(navItem.type === 'GROUP' && {
        items: navItem.items.map((item) => createAppearanceItem(item)),
      }),
    });

    return navItems.map(createAppearanceItem);
  };

  static getVisibilityByBreakPointVisibility = ({
    resolution,
    breakpointVisibility,
  }: {
    resolution: NavigationResolution;
    breakpointVisibility?: NavigationResolution[];
  }) => {
    if (!breakpointVisibility) return true;

    return breakpointVisibility.includes(resolution);
  };

  static transformAppearanceNavItemToNavItems = ({
    appearanceItems,
    originalNavItems,
  }: {
    appearanceItems: AppearanceNavItems;
    originalNavItems: NavItems;
  }): NavItemsWithVisibilityAndOverflow => {
    const createNavItemMap = (items: NavItems): Map<string, NavItem> => {
      return items.reduce((map, item) => {
        map.set(item.id, item);
        if (item.type === 'GROUP') {
          // Merge nested maps
          const nestedMap = createNavItemMap(item.items);
          for (const [key, value] of nestedMap) {
            map.set(key, value);
          }
        }
        return map;
      }, new Map<string, NavItem>());
    };
    const navItemMap = createNavItemMap(originalNavItems);

    const convertToNavItem = (
      appearanceItem: AppearanceNavItem,
    ): NavItemWithVisibilityAndOverflow => {
      const originalNavItem = navItemMap.get(appearanceItem.id);
      if (!originalNavItem) {
        throw new Error(
          `No matching NavItem found for appearance item with id: ${appearanceItem.id}`,
        );
      }

      if (appearanceItem.items && originalNavItem.type === 'GROUP') {
        return {
          ...originalNavItem,
          hiddenInOverflow: appearanceItem.hiddenInOverflow ?? false,
          ...('visible' in appearanceItem && { visible: appearanceItem.visible }),
          items: appearanceItem.items.flat().map((item) => convertToNavItem(item)),
        } as NavItemWithVisibilityAndOverflow;
      }

      return {
        ...originalNavItem,
        hiddenInOverflow: appearanceItem.hiddenInOverflow ?? false,
        ...('visible' in appearanceItem && { visible: appearanceItem.visible }),
      } as NavItemWithVisibilityAndOverflow;
    };

    return appearanceItems.map(convertToNavItem);
  };

  // Creates a map of navItems with id as key and navItem as value
  static createNavItemsMap = (navItems?: NavItems) => {
    const createNavItemMap = (items?: NavItems): Map<string, NavItem> | undefined => {
      return items?.reduce((map, item) => {
        map.set(item.id, item);
        if (item.type === 'GROUP') {
          // Merge nested maps
          const nestedMap = createNavItemMap(item.items);
          if (nestedMap) {
            for (const [key, value] of nestedMap) {
              map.set(key, value);
            }
          }
        }
        return map;
      }, new Map<string, NavItem>());
    };
    return createNavItemMap(navItems);
  };

  // Collects all nav item IDs from primary and secondary nav items (including deeply nested items in groups)
  static collectAllNavItemIds = (navigation: Navigation): Set<string> => {
    const navItemsMap = InterfaceNavigationHelper.createNavItemsMap([
      ...navigation.primary,
      ...(navigation.secondary ?? []),
    ]);
    return new Set(navItemsMap?.keys() ?? []);
  };

  /**
   * Cleans up entityDetailsMap by removing entries for modules that are no longer in the navigation
   * @param navigation - the navigation object
   * @param entityDetailsMap - the current entityDetailsMap from interface properties
   * @returns cleaned entityDetailsMap or undefined if empty
   */
  static cleanupEntityDetailsMap = (
    navigation: Navigation,
    entityDetailsMap?: Record<string, EntityDetails>,
  ): Record<string, EntityDetails> | undefined => {
    if (!entityDetailsMap) {
      return undefined;
    }

    // get all module IDs currently in navigation
    const moduleIdsInNavigation = new Set<string>();
    const collectModuleIds = (items: NavItems) => {
      items.forEach((item) => {
        if (item.type === 'MODULE') {
          moduleIdsInNavigation.add(item.moduleId);
        }
        if (item.type === 'GROUP') {
          collectModuleIds(item.items);
        }
      });
    };

    const isValidModuleOrPageEntry = (_details: EntityDetails, moduleId: string): boolean => {
      const entityDetailsType = InterfaceHelper.getEntityDetailsType(_details);

      return (
        // keep the entry if it's a module that still exists in navigation or
        (entityDetailsType === InterfacePageEntityComponentType.MODULE &&
          moduleIdsInNavigation.has(moduleId)) ||
        //  if it's a page
        entityDetailsType === InterfacePageEntityComponentType.PAGE
      );
    };

    collectModuleIds(navigation.primary);
    if (navigation.secondary) {
      collectModuleIds(navigation.secondary);
    }

    // keep only entries for modules that still exist in
    // navigation or the entries which are pages
    const cleanedMap = _pickBy(entityDetailsMap, isValidModuleOrPageEntry);

    return Object.keys(cleanedMap).length > 0 ? cleanedMap : undefined;
  };

  // If color value exist in interfaceRecord then use that value, else read from variables applied to document
  static getThemeColors = ({
    interfaceRecord,
    variant,
    theme,
    isExpandableSolid,
  }: {
    interfaceRecord: InterfaceEntity;
    variant: NavigationSelectionVariant;
    theme: NavigationTheme;
    isExpandableSolid: boolean;
  }) => {
    const colorsTheme = interfaceRecord.properties.theme?.components?.[ThemeComponents.Navigation];
    const { background, icon, text } = colorsTheme?.navigationItem?.[variant]?.[theme] ?? {};

    const elementStyles = getInterfaceCssVarElementStyles();

    const getCssValue = (token: string) => elementStyles?.getPropertyValue(token);

    return {
      backgroundColors: {
        default:
          background?.default ??
          getCssValue(
            (isExpandableSolid ? EXPANDED_SOLID_NAVIGATION_TOKENS : NAVIGATION_TOKENS).background
              .default,
          ),
        hover:
          background?.hover ??
          getCssValue(
            (isExpandableSolid ? EXPANDED_SOLID_NAVIGATION_TOKENS : NAVIGATION_TOKENS).background
              .hover,
          ),
        active:
          background?.active ??
          getCssValue(
            (isExpandableSolid ? EXPANDED_SOLID_NAVIGATION_TOKENS : NAVIGATION_TOKENS).background
              .active,
          ),
      },
      iconColors: {
        default:
          icon?.default ??
          getCssValue(
            (isExpandableSolid ? EXPANDED_SOLID_NAVIGATION_TOKENS : NAVIGATION_TOKENS).icon.default,
          ),
        hover:
          icon?.hover ??
          getCssValue(
            (isExpandableSolid ? EXPANDED_SOLID_NAVIGATION_TOKENS : NAVIGATION_TOKENS).icon.hover,
          ),
        active: icon?.active ?? getCssValue(NAVIGATION_TOKENS.icon.active),
      },
      textColors: {
        default:
          text?.default ??
          getCssValue(
            (isExpandableSolid ? EXPANDED_SOLID_NAVIGATION_TOKENS : NAVIGATION_TOKENS).text.default,
          ),
        hover:
          text?.hover ??
          getCssValue(
            (isExpandableSolid ? EXPANDED_SOLID_NAVIGATION_TOKENS : NAVIGATION_TOKENS).text.hover,
          ),
        active:
          text?.active ??
          getCssValue(
            (isExpandableSolid ? EXPANDED_SOLID_NAVIGATION_TOKENS : NAVIGATION_TOKENS).text.active,
          ),
      },
    };
  };

  // Populates the color in NavigationAppearance FormData
  static getNavigationItemWithThemeColors = ({
    interfaceRecord,
    navigationItem,
    theme,
  }: {
    interfaceRecord: InterfaceEntity;
    navigationItem: NavigationItem;
    theme: NavigationTheme;
  }) => {
    const navbarVariant = navigationItem.selectionVariant;
    const labelVariant = navigationItem.labelVariant;
    const selectionVariant = navigationItem.selectionVariant;
    const isExpandableSolid =
      labelVariant === NavigationItemLabelVariant.Expandable &&
      selectionVariant === NavigationSelectionVariant.Solid;

    const { backgroundColors, iconColors, textColors } = this.getThemeColors({
      interfaceRecord,
      variant: navbarVariant,
      theme,
      isExpandableSolid,
    });

    return {
      ...navigationItem,
      background: { colors: backgroundColors },
      icon: { ...navigationItem.icon, colors: iconColors },
      text: { ...navigationItem.text, colors: textColors },
    };
  };

  // When visibility of navItem is toggled, we update the breakpoint visibility of the navItem
  // Also remove the visible prop from the navItem for that resolution
  static getNavigationWithUpdatedBreakpointVisibility = ({
    navigation,
    category,
    navItem,
    resolution,
    updatedVisibility,
  }: {
    navigation: Navigation;
    category: NavigationItemCategory;
    navItem: NavItemWithVisibilityAndOverflow;
    resolution: NavigationResolution;
    updatedVisibility: boolean;
  }) => {
    const { primary, secondary, appearance } = navigation;
    let navItemsToUpdate: NavItems | undefined;
    if (category === NavigationItemCategory.Primary) {
      navItemsToUpdate = primary;
    } else if (category === NavigationItemCategory.Secondary) {
      navItemsToUpdate = secondary;
    } else if (category === NavigationItemCategory.UserMenu) {
      navItemsToUpdate = secondary?.find((item) => item.type === 'USER_MENU')?.menuItems;
    }

    const _itemIdToUpdate = navItem.id;

    const removeVisibleFromNavitem = ({
      currentNavItem,
      resolutionAppearanceItems,
    }: {
      currentNavItem: NavItemWithVisibilityAndOverflow;
      resolutionAppearanceItems?: AppearanceNavItems;
    }) => {
      return resolutionAppearanceItems?.map((item) => {
        if (item.items) {
          const updatedGroupItem = item.id === currentNavItem.id ? _omit(item, 'visible') : item;
          const updatedGroupSubItems = item.items.map((subItem) => {
            return subItem.id === currentNavItem.id ? _omit(subItem, 'visible') : subItem;
          });
          return {
            ...updatedGroupItem,
            items: updatedGroupSubItems,
          };
        }
        return item.id === currentNavItem.id ? _omit(item, 'visible') : item;
      });
    };

    const resolutionAppearanceItems = appearance?.[resolution]?.[category];

    const updatedResolutionAppearanceItems = removeVisibleFromNavitem({
      currentNavItem: navItem,
      resolutionAppearanceItems,
    });

    const getBreakPointVisibility = (
      _resolution: NavigationResolution,
      visible: boolean,
      breakpointVisibility?: NavigationResolution[],
    ) => {
      if (!breakpointVisibility) {
        return visible
          ? DEFAULT_BREAKPOINT_VISIBILITY
          : DEFAULT_BREAKPOINT_VISIBILITY.filter((res) => res !== _resolution);
      }

      if (visible) {
        return breakpointVisibility.includes(_resolution)
          ? breakpointVisibility
          : [...breakpointVisibility, _resolution];
      }

      return breakpointVisibility.filter((res) => res !== resolution);
    };

    const updateItemBreakpointVisibility = (
      item: NavItem,
      itemIdToUpdate: string,
      _resolution: NavigationResolution,
      isVisible: boolean,
    ) => {
      if (item.type === 'GROUP') {
        const updatedGroupItem =
          item.id === itemIdToUpdate
            ? {
                ...item,
                breakpointVisibility: getBreakPointVisibility(
                  _resolution,
                  isVisible,
                  item.breakpointVisibility,
                ),
              }
            : item;
        const updatedGroupSubItems = item.items.map((subItem) => {
          return subItem.id === itemIdToUpdate
            ? {
                ...subItem,
                breakpointVisibility: getBreakPointVisibility(
                  _resolution,
                  isVisible,
                  subItem.breakpointVisibility,
                ),
              }
            : subItem;
        });
        return {
          ...updatedGroupItem,
          items: updatedGroupSubItems,
        };
      }

      return item.id === itemIdToUpdate
        ? {
            ...item,
            breakpointVisibility: getBreakPointVisibility(
              _resolution,
              isVisible,
              item.breakpointVisibility,
            ),
          }
        : item;
    };

    const navItemsWithUpdatedBreakpointVisibility = navItemsToUpdate?.map((item) =>
      updateItemBreakpointVisibility(item, _itemIdToUpdate, resolution, updatedVisibility),
    );

    const updatedNavigation = {
      ...navigation,
      appearance: {
        ...navigation.appearance,
        [resolution]: {
          ...navigation.appearance?.[resolution],
          [category]: updatedResolutionAppearanceItems,
        },
      },
      ...(category !== NavigationItemCategory.UserMenu
        ? { [category]: navItemsWithUpdatedBreakpointVisibility }
        : {}),
      ...(category === NavigationItemCategory.UserMenu
        ? {
            secondary: produce(navigation.secondary, (draftSecondaryNavItems) => {
              if (!draftSecondaryNavItems) {
                return draftSecondaryNavItems;
              }

              const userMenuIndex = draftSecondaryNavItems?.findIndex(
                (item) => item.type === 'USER_MENU',
              );
              if (userMenuIndex !== undefined && userMenuIndex !== -1) {
                draftSecondaryNavItems[userMenuIndex] = {
                  ...draftSecondaryNavItems[userMenuIndex],
                  menuItems: navItemsWithUpdatedBreakpointVisibility as (
                    | UrlNavItem
                    | PageNavItem
                  )[],
                } as UserMenuNavItem;
              }
              return draftSecondaryNavItems;
            }),
          }
        : {}),
    };

    return updatedNavigation;
  };
}
